(() => {
  if (customElements.get('laydown-matrix-backdrop')) return;
  class MatrixBackdrop extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.renderer = null;
      this.loading = false;
      this.failed = false;
      this.visible = false;
      this.frame = 0;
      this.setupFrame = requestAnimationFrame(() => this.setup());
    }
    setup() {
      if (!this.isConnected) return;
      this.technical = this.closest('.shopify-section');
      this.editorial = this.technical?.nextElementSibling;
      this.heading = Array.from(this.editorial?.querySelectorAll('h2') || []).find(el => el.textContent.trim().toUpperCase() === 'A WINDOW LEFT OPEN.');
      if (!this.heading || !this.editorial.querySelector('.media-with-content')) return;
      this.editorial.classList.add('laydown-matrix-editorial');
      this.canvas = this.querySelector('canvas');
      this.poster = this.querySelector('img');
      this.entry = this.querySelector('.laydown-matrix-backdrop__entry');
      this.resizeObserver = new ResizeObserver(() => this.scheduleLayout());
      [this.technical, this.editorial, this.heading].forEach(el => this.resizeObserver.observe(el));
      this.layout();
      this.nearObserver = new IntersectionObserver(entries => {
        if (entries[0].isIntersecting) { this.load(); this.nearObserver.disconnect(); }
      }, {rootMargin:'250px'});
      this.nearObserver.observe(this.editorial);
      this.visibleObserver = new IntersectionObserver(entries => {
        this.visible = entries[0].isIntersecting;
        this.sync();
      });
      this.visibleObserver.observe(this.editorial);
      const options = {signal:this.controller.signal};
      document.addEventListener('visibilitychange', () => this.sync(), options);
      this.motion.addEventListener('change', () => this.sync(), options);
      window.addEventListener('resize', () => this.scheduleLayout(), options);
      this.canvas.addEventListener('webglcontextlost', event => {
        event.preventDefault(); this.renderer?.destroy(); this.renderer = null; this.failed = true; this.showPoster();
      }, options);
    }
    scheduleLayout() {
      cancelAnimationFrame(this.frame);
      this.frame = requestAnimationFrame(() => this.layout());
    }
    layout() {
      const anchorTop = this.technical.getBoundingClientRect().top;
      const top = this.editorial.getBoundingClientRect().top;
      const end = this.editorial.getBoundingClientRect().bottom - top;
      const textTop = this.heading.getBoundingClientRect().top - top;
      const mobile = matchMedia('(max-width: 749px)').matches;
      const fadeEnd = mobile ? Math.max(0, textTop - 28) : end;
      const fadeStart = Math.max(0, fadeEnd - (mobile ? 220 : 170));
      this.style.setProperty('--matrix-top', `${top - anchorTop}px`);
      this.style.setProperty('--matrix-height', `${Math.ceil(end)}px`);
      this.style.setProperty('--matrix-fade-start', `${Math.round(fadeStart)}px`);
      this.style.setProperty('--matrix-fade-end', `${Math.round(fadeEnd)}px`);
      this.dataset.fadeEnd = Math.round(fadeEnd);
      this.dataset.textTop = Math.round(textTop);
      this.updateEntryMask();
    }
    columnCount() {
      const mobile = this.clientWidth < 700;
      const previousHeight = this.clientHeight + this.technical.getBoundingClientRect().height;
      const sizeRatio = Math.max(this.clientWidth, this.clientHeight) / Math.max(this.clientWidth, previousHeight);
      return Math.max(24, Math.round((mobile ? 66 : 78) * sizeRatio));
    }
    updateEntryMask() {
      if (!this.entry) return;
      const width = Math.ceil(this.clientWidth), height = Math.ceil(this.clientHeight);
      if (!width || !height) return;
      const columns = this.numColumns || this.columnCount();
      const key = `${width}:${height}:${columns}`;
      if (key === this.entryMaskKey) return;
      this.entryMaskKey = key;
      const cell = Math.max(width, height) / columns;
      let gradients = '', rectangles = '';
      for (let i = 0; i < Math.ceil(width / cell); i++) {
        const offset = Math.abs((Math.sin(i * 127.1 + 37.7) * 43758.5453) % 1) * 55;
        gradients += `<linearGradient id="g${i}" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="white" stop-opacity="0"/><stop offset="${offset / height}" stop-color="white" stop-opacity="0"/><stop offset="${(offset + 90) / height}" stop-color="white" stop-opacity="1"/><stop offset="1" stop-color="white" stop-opacity="1"/></linearGradient>`;
        rectangles += `<rect x="${i * cell}" width="${cell + 0.1}" height="${height}" fill="url(#g${i})"/>`;
      }
      const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${width} ${height}" preserveAspectRatio="none"><defs>${gradients}</defs>${rectangles}</svg>`;
      this.entry.style.setProperty('--matrix-entry-mask', `url("data:image/svg+xml,${encodeURIComponent(svg)}")`);
      this.dataset.entryDirection = '4';
    }
    async load() {
      if (this.loading || this.renderer || this.failed || !this.isConnected) return;
      this.loading = true;
      if (this.motion.matches) { this.showPoster(); this.loading = false; return; }
      this.dataset.state = 'loading';
      try {
        const {createRain} = await import(this.dataset.engine);
        if (!this.isConnected || this.motion.matches) { this.showPoster(); return; }
        this.canvas.hidden = false;
        // Preserve the approved glyph scale when removing the technical area from the canvas.
        const numColumns = this.columnCount();
        this.numColumns = numColumns;
        this.updateEntryMask();
        const renderer = await createRain(this.canvas, {glyphMSDFURL:this.dataset.atlas, numColumns});
        if (!this.isConnected) { renderer.destroy(); return; }
        this.renderer = renderer;
        this.poster.hidden = true;
        this.sync();
      } catch (error) {
        this.failed = true;
        this.showPoster();
        console.warn('LAYDOWN rain uses its static fallback:', error.message);
      } finally { this.loading = false; }
    }
    showPoster() {
      if (!this.poster || !this.isConnected) return;
      this.canvas.hidden = true;
      if (!this.poster.getAttribute('src')) this.poster.src = this.dataset.poster;
      this.poster.hidden = false;
      this.dataset.state = 'static';
    }
    sync() {
      if (!this.isConnected) return;
      if (this.motion.matches) { this.renderer?.pause(); this.showPoster(); return; }
      if (!this.renderer) {
        if (this.visible && !this.loading) this.load();
        return;
      }
      this.canvas.hidden = false;
      this.poster.hidden = true;
      const running = this.visible && !document.hidden;
      this.renderer[running ? 'start' : 'pause']();
      this.dataset.state = running ? 'running' : 'paused';
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      cancelAnimationFrame(this.setupFrame);
      cancelAnimationFrame(this.frame);
      this.resizeObserver?.disconnect();
      this.nearObserver?.disconnect();
      this.visibleObserver?.disconnect();
      this.renderer?.destroy();
      this.renderer = null;
      this.editorial?.classList.remove('laydown-matrix-editorial');
    }
  }
  customElements.define('laydown-matrix-backdrop', MatrixBackdrop);
})();
